import { FC, useCallback, useMemo } from 'react';

import { useFormikContext } from 'formik';
import { Button } from 'ui/components/common';
import { SelectAvatar } from 'ui/components/form';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import useModal from 'ui/partials/Modal/useModal';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

import { useGetLeads } from '../hooks';
import { LEAD_ONLY_FORM } from './schema';

const LeadOnlyForm: FC = () => {
  const { data: leads } = useGetLeads();

  const { values, handleSubmit, setFieldValue, isSubmitting } =
    useFormikContext<LEAD_ONLY_FORM>();

  const { hideModal } = useModal();

  const dealItems = useCallback(
    (search: string) =>
      (leads?.data
        ?.map((lead: any) => ({
          photoUrl: lead?.photoUrl,
          itemLabel: lead?.fullName,
          description: `${lead?.emailAddress}`,
          value: lead.id,
        }))
        .filter(
          (ld) =>
            ld.itemLabel.toLowerCase().includes(search.toLowerCase()) ||
            ld.description.toLowerCase().includes(search.toLowerCase()),
        ) ?? []) as AvatarOption[],
    [leads],
  );

  const loadDealItems = (
    search = '',
    cb: (options: AvatarOption[]) => void,
  ) => {
    cb(dealItems(search));
  };

  const defaultDealId = useMemo(
    () =>
      leads?.data
        ?.filter((lead) => values.leadId?.includes(lead.id))
        .map((lead: any) => ({
          photoUrl: lead?.photoUrl,
          itemLabel: lead?.fullName,
          description: `${lead?.emailAddress}`,
          value: lead.id,
        })),
    [leads, values.leadId],
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-y-4">
        <SelectAvatar
          className="w-full"
          label="Contacts"
          onChange={(val) =>
            setFieldValue(
              'leadId',
              (val as AvatarOption[]).map((v) => v.value),
            )
          }
          loadOptions={loadDealItems}
          onClear={() => setFieldValue('leadId', [])}
          size={SIZE.Small}
          name="leadId"
          isMulti
          placeholder="Select Contacts"
          defaultOptions
          defaultValue={defaultDealId as any}
        />
      </div>
      <div
        className={cn(
          'pt-[22px] mt-[22px]',
          'flex justify-end gap-2',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={() => hideModal()}
          label="CANCEL"
          intent={'tertiary'}
          disabled={isSubmitting}
        />
        <Button
          type="submit"
          label="SAVE"
          className="primary sm"
          disabled={isSubmitting}
        />
      </div>
    </form>
  );
};

export default LeadOnlyForm;
